import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Skeleton, Tabs} from 'antd';
import {CarOutlined} from '@ant-design/icons';

import CarGeneralInfoStaff from './CarGeneralInfoStaff';
import CarAccessories from './CarAccessories';
import CarMove from './CarMove';
import CarTripLog from './CarTripLog';
import TripLogEntry from './TripLogEntry';
import CarImageUpload from '../../../components/car/CarImageUpload';
import DetailsScreenHeader from '../../../components/detailsScreenHeader/DetailsScreenHeader';
import ErrorBoundary from '../../../components/ErrorBoundary';
import Error404 from '../../../components/errors/404';
import Comments from '../Comments';
import PageTitle from '../../../components/page/PageTitle';
import {useGetCompanyInfoQuery} from '../../../store/api/settingsApi';
import CarTelematics from './CarTelematics';
import CarHardwareLog from './CarHardwareLog';
import {useAppSelector} from '../../../hooks/useStore';
import {selectHasStaffRole} from '../../../store/slices/userSlice';
import {useGetCarQuery} from '../../../store/api/carsApi';

const CarManageStaff = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const isMCT = useAppSelector((state) => selectHasStaffRole(state, ['member-care', 'developer', 'fleet']));

    const {data: clientInfo} = useGetCompanyInfoQuery();
    const hardwareTelematicsEnabled = !!clientInfo?.hardware_telematics_enabled ?? false;

    const carID = Number(new URLSearchParams(location.search).get('id'));
    const {data: car, isSuccess, isError, isLoading} = useGetCarQuery(carID);

    const tabsItems = React.useMemo(() => {
        if (!car) {
            return [];
        }

        let items = [
            {
                label: 'General Information',
                key: '1',
                children:
                    <ErrorBoundary>
                        <CarGeneralInfoStaff
                            car={car}
                            isStaff={true}
                        />
                    </ErrorBoundary>
            },
            {
                label: 'Accessories',
                key: '2',
                children:
                    <ErrorBoundary>
                        <CarAccessories
                            car={car}
                        />
                    </ErrorBoundary>
            },
            {
                label: 'Vehicle Moves',
                key: '3',
                children:
                    <ErrorBoundary>
                        <CarMove carID={car.id}/>
                    </ErrorBoundary>
            },
            {
                label: 'Manual Trip Log Entry',
                key: '4',
                children:
                    <ErrorBoundary>
                        <TripLogEntry carID={car.id}/>
                    </ErrorBoundary>
            },
            {
                label: 'Comments',
                key: '5',
                children:
                    <ErrorBoundary>
                        <Comments
                            commentableType='App\Models\Bookables\BookCar'
                            commentableID={car.id}
                        />
                    </ErrorBoundary>
            },
        ];

        let hardwareTelematicsJSX =
            {
                label: 'Telematics',
                key: '6',
                children:
                    <ErrorBoundary>
                        <CarTelematics carID={car.id}/>
                    </ErrorBoundary>
            }

        let onboardLogJSX =
            {
                label: 'Onboard Log',
                key: '7',
                children:
                    <ErrorBoundary>
                        <CarHardwareLog carID={car.id}/>
                    </ErrorBoundary>
            }

        let tripLogJSX =
            {
                label: 'Trip Log',
                key: '8',
                children:
                    <ErrorBoundary>
                        <CarTripLog carID={car.id}/>
                    </ErrorBoundary>
            }

        // Only display hardware telematics tabs if the CSO uses them.
        if (hardwareTelematicsEnabled) {
            items.splice(3, 0, hardwareTelematicsJSX)
            // Only populate the onboard/hardware log tab if it's MCT or Fleet staff.
            if (isMCT) {
                items.splice(4, 0, onboardLogJSX)
                items.splice(6, 0, tripLogJSX)
            }
        }

        return items;
    }, [car, isMCT, hardwareTelematicsEnabled]);

    const onClickBack = () => {
        const {state} = location;
        if (!state) {
            navigate(-1);
            return;
        }

        if (state && state.page && state.perPage) {
            navigate('/fleet/car/list', {
                state: {page: state.page, perPage: state.perPage},
            });
        } else {
            navigate('/fleet/car/list');
        }
    };

    if (!isSuccess || isError) return <Error404/>;

    return (
        (isLoading || tabsItems.length === 0) ?
            <Skeleton active loading={isLoading}/> :
                <>
                    <PageTitle title="Vehicle Details" icon={<CarOutlined/>}/>
                    <DetailsScreenHeader car={car} onClickBack={onClickBack}>
                        <CarImageUpload
                            car={car}
                        />
                    </DetailsScreenHeader>
                    <Tabs items={tabsItems}/>
                </>
    );
}

export default CarManageStaff;
